import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable, { applyPlugin } from "jspdf-autotable";
import { useCompany } from "../../context/CompanyContext";
import { showToast } from "../../utils/customToast";
import {
  FaSearch,
  FaFileExcel,
  FaFilePdf,
  FaPrint,
  FaClock,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import "./SalesRegister.css";
import { TableRowsSkeleton } from "../../Component/Skeleton";

try {
  applyPlugin(jsPDF);
} catch (e) {
  // autoTable plugin already applied or fallback
}

// Format numbers in standard Indian Rupee format
const formatINR = (val) => {
  if (val === null || val === undefined || isNaN(val)) return "";
  const num = Number(val);
  if (num === 0) return "";
  return num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export default function SalesRegister() {
  const navigate = useNavigate();
  const { selectedCompany } = useCompany();
  const BASE_API_URL =
    process.env.REACT_APP_BASE_API_URL ||
    process.env.REACT_APP_API_URL ||
    "http://localhost:5000/api";

  const companyId = useMemo(() => {
    return (
      selectedCompany?.CompID ||
      selectedCompany?.id ||
      sessionStorage.getItem("CompID") ||
      sessionStorage.getItem("companyId") ||
      "1147"
    );
  }, [selectedCompany]);

  const companyInfo = useMemo(() => {
    return {
      name:
        selectedCompany?.Name ||
        selectedCompany?.name ||
        selectedCompany?.CompanyName ||
        "DAZY TOURE TRAVEL'S",
      address:
        selectedCompany?.Address ||
        selectedCompany?.address ||
        "Opp. GPO, M.I. Road, Jaipur-302001",
      gstin: selectedCompany?.GSTNO || selectedCompany?.gstin || "",
      stateCode: selectedCompany?.StateCode || selectedCompany?.stateCode || "08",
    };
  }, [selectedCompany]);

  // Default dates: 1st of current month to today
  const defaultDates = useMemo(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    const d = String(now.getDate()).padStart(2, "0");
    return {
      fromDate: `${y}-${m}-01`,
      toDate: `${y}-${m}-${d}`,
    };
  }, []);

  const [fromDate, setFromDate] = useState(defaultDates.fromDate);
  const [toDate, setToDate] = useState(defaultDates.toDate);
  const [activeFilters, setActiveFilters] = useState({
    fromDate: defaultDates.fromDate,
    toDate: defaultDates.toDate,
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [liveData, setLiveData] = useState([]);
  const [loading, setLoading] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Live timestamp for "As on" badge
  const asOnTime = useMemo(() => {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, "0");
    const mNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const mon = mNames[now.getMonth()];
    const year = now.getFullYear();
    const hours = now.getHours();
    const mins = String(now.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    const h12 = hours % 12 || 12;
    return `${day}-${mon}-${year} ${h12}:${mins} ${ampm}`;
  }, []);

  // Fetch sales records
  const fetchSalesRecords = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${BASE_API_URL}/reports/sales-register/${companyId}?fromDate=${activeFilters.fromDate}&toDate=${activeFilters.toDate}`
      );
      if (res.data?.success && Array.isArray(res.data.data) && res.data.data.length > 0) {
        setLiveData(res.data.data);
      } else {
        setLiveData([]);
      }
    } catch (err) {
      setLiveData([]);
    } finally {
      setLoading(false);
    }
  }, [BASE_API_URL, companyId, activeFilters]);

  useEffect(() => {
    fetchSalesRecords();
  }, [fetchSalesRecords]);

  const handleApplyFilter = () => {
    setActiveFilters({ fromDate, toDate });
    setCurrentPage(1);
  };

  const handleReset = () => {
    setFromDate(defaultDates.fromDate);
    setToDate(defaultDates.toDate);
    setActiveFilters({ fromDate: defaultDates.fromDate, toDate: defaultDates.toDate });
    setSearchTerm("");
    setCurrentPage(1);
  };

  // Filter records by search term
  const filteredRecords = useMemo(() => {
    return liveData.filter((r) => {
      if (activeFilters.fromDate && r.rawDate && r.rawDate < activeFilters.fromDate) return false;
      if (activeFilters.toDate && r.rawDate && r.rawDate > activeFilters.toDate) return false;
      if (searchTerm.trim()) {
        const term = searchTerm.toLowerCase();
        const matchInvoice = (r.invoiceNo || r.voucherNo) && (r.invoiceNo || r.voucherNo).toLowerCase().includes(term);
        const matchParty = (r.partyName || r.particulars) && (r.partyName || r.particulars).toLowerCase().includes(term);
        return matchInvoice || matchParty;
      }
      return true;
    });
  }, [liveData, activeFilters, searchTerm]);

  // Compute Totals
  const totals = useMemo(() => {
    let debit = 0;
    let credit = 0;
    filteredRecords.forEach((r) => {
      debit += Number(r.debit || 0);
      credit += Number(r.credit || 0);
    });
    return { debit, credit, count: filteredRecords.length };
  }, [filteredRecords]);

  // Pagination calculation
  const totalPages = Math.max(1, Math.ceil(filteredRecords.length / rowsPerPage));
  const currentTableData = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return filteredRecords.slice(start, start + rowsPerPage);
  }, [filteredRecords, currentPage, rowsPerPage]);

  // Export to Excel (.xlsx)
  const handleExportExcel = () => {
    try {
      const activeCompName = String(companyInfo.name).toUpperCase();
      const compDetails = `Address: ${companyInfo.address || ""}  |  GSTIN: ${companyInfo.gstin || ""}  |  State: ${companyInfo.stateCode || "08"}`;
      const reportTitle = "SALES REGISTER";
      const periodSubtitle = `Period: ${activeFilters.fromDate} to ${activeFilters.toDate}  |  Generated: ${asOnTime}`;

      const headers = [
        "Date",
        "Invoice No.",
        "Particulars / Narration",
        "Debit (INR)",
        "Credit (INR)",
      ];

      const dataRows = [];
      filteredRecords.forEach((r) => {
        const debitLines = (r.lines || []).filter((l) => l.type === "Dr");
        const creditLines = (r.lines || []).filter((l) => l.type === "Cr");

        if (debitLines.length === 0 && creditLines.length === 0) {
          const firstDebit = r.firstDebit || r.particulars || "Sales Account";
          const firstCredit = r.firstCredit ? `  ${r.firstCredit}` : `  ${r.partyName || r.particulars || "Party"}`;
          dataRows.push([
            r.date,
            r.invoiceNo || r.voucherNo || "",
            firstDebit,
            Number(r.debit) > 0 ? Number(r.debit) : "",
            "",
          ]);
          if (Number(r.credit) > 0) {
            dataRows.push([
              "",
              "",
              firstCredit,
              "",
              Number(r.credit),
            ]);
          }
        } else {
          if (debitLines.length === 0) {
            dataRows.push([
              r.date,
              r.invoiceNo || r.voucherNo || "",
              r.firstDebit || r.particulars || "Sales Account",
              Number(r.debit) > 0 ? Number(r.debit) : "",
              "",
            ]);
          } else {
            debitLines.forEach((dr, idx) => {
              const drAmt = Number(dr.debit) || Number(r.debit) || 0;
              dataRows.push([
                idx === 0 ? r.date : "",
                idx === 0 ? (r.invoiceNo || r.voucherNo || "") : "",
                dr.ledgerName,
                drAmt > 0 ? drAmt : "",
                "",
              ]);
            });
          }

          creditLines.forEach((cr, idx) => {
            const crAmt = Number(cr.credit) || Number(r.credit) || 0;
            dataRows.push([
              debitLines.length === 0 && idx === 0 ? r.date : "",
              debitLines.length === 0 && idx === 0 ? (r.invoiceNo || r.voucherNo || "") : "",
              `  ${cr.ledgerName}`,
              "",
              crAmt > 0 ? crAmt : "",
            ]);
          });
        }

        if (r.narration) {
          dataRows.push([
            "",
            "",
            `  (Narration: ${r.narration})`,
            "",
            "",
          ]);
        }
      });

      const totalRow = [
        "Total",
        "",
        "",
        Number(totals.debit) || 0,
        Number(totals.credit) || 0,
      ];

      const wsData = [
        [activeCompName],
        [compDetails],
        [reportTitle],
        [periodSubtitle],
        [],
        headers,
        ...dataRows,
        totalRow,
        [],
        ["etour - Design & Develop by Response Infoway"],
      ];

      const worksheet = XLSX.utils.aoa_to_sheet(wsData);
      worksheet["!merges"] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: 4 } },
        { s: { r: 1, c: 0 }, e: { r: 1, c: 4 } },
        { s: { r: 2, c: 0 }, e: { r: 2, c: 4 } },
        { s: { r: 3, c: 0 }, e: { r: 3, c: 4 } },
      ];

      worksheet["!cols"] = [
        { wch: 15 }, // Date
        { wch: 18 }, // Invoice No
        { wch: 55 }, // Particulars / Narration
        { wch: 20 }, // Debit
        { wch: 20 }, // Credit
      ];

      // Format number cells in worksheet with standard Indian 2-decimal format
      const range = XLSX.utils.decode_range(worksheet["!ref"]);
      for (let R = 5; R <= range.e.r; ++R) {
        const cellDr = worksheet[XLSX.utils.encode_cell({ r: R, c: 3 })];
        if (cellDr && typeof cellDr.v === "number") {
          cellDr.z = "#,##0.00";
        }
        const cellCr = worksheet[XLSX.utils.encode_cell({ r: R, c: 4 })];
        if (cellCr && typeof cellCr.v === "number") {
          cellCr.z = "#,##0.00";
        }
      }

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Sales_Register");
      const fileName = `Sales_Register_${activeFilters.fromDate}_to_${activeFilters.toDate}.xlsx`;
      XLSX.writeFile(workbook, fileName);
      showToast.success("Excel exported successfully");
    } catch (err) {
      console.error(err);
      showToast.error("Failed to export Excel");
    }
  };

  // Export to PDF
  const handleExportPDF = () => {
    try {
      const doc = new jsPDF("p", "mm", "a4");
      const pageWidth = doc.internal.pageSize.getWidth();
      const activeCompName = String(companyInfo.name).toUpperCase();
      const compLine2 = [
        companyInfo.address,
        companyInfo.gstin ? `GSTIN: ${companyInfo.gstin}` : "",
        companyInfo.stateCode ? `State Code: ${companyInfo.stateCode}` : "",
      ]
        .filter(Boolean)
        .join("   |   ");
      const reportTitle = "SALES REGISTER";

      // 1. Company Name Header
      doc.setFont("helvetica", "bold");
      doc.setFontSize(15);
      doc.setTextColor(0, 0, 0);
      doc.text(activeCompName, pageWidth / 2, 14, { align: "center" });

      // 2. Company Details
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      doc.setTextColor(40, 40, 40);
      doc.text(compLine2, pageWidth / 2, 19, { align: "center" });

      // 3. Report Title
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10.5);
      doc.setTextColor(0, 0, 0);
      doc.text(reportTitle, pageWidth / 2, 26, { align: "center" });
      const titleWidth = doc.getTextWidth(reportTitle);
      doc.setLineWidth(0.75);
      doc.setDrawColor(0, 0, 0);
      doc.line(
        pageWidth / 2 - titleWidth / 2,
        27.5,
        pageWidth / 2 + titleWidth / 2,
        27.5
      );

      // 4. Divider Line
      doc.setLineWidth(1);
      doc.setDrawColor(0, 0, 0);
      doc.line(14, 31, pageWidth - 14, 31);

      // 5. Meta Information Bar
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8.5);
      doc.setTextColor(0, 0, 0);
      doc.text(`Period: ${activeFilters.fromDate} to ${activeFilters.toDate}`, 14, 36);
      doc.text(`Generated: ${asOnTime}`, pageWidth - 14, 36, { align: "right" });

      // 6. Divider Line below Meta bar
      doc.setLineWidth(0.5);
      doc.setDrawColor(0, 0, 0);
      doc.line(14, 39, pageWidth - 14, 39);

      const tableRows = filteredRecords.map((r) => [
        r.date,
        r.invoiceNo || r.voucherNo,
        r.partyName || r.particulars,
        formatINR(r.debit),
        formatINR(r.credit),
      ]);

      tableRows.push([
        "",
        "",
        "Total",
        formatINR(totals.debit),
        formatINR(totals.credit),
      ]);

      autoTable(doc, {
        head: [
          [
            "Date",
            "Invoice No.",
            "Party Name",
            "Debit (INR)",
            "Credit (INR)",
          ],
        ],
        body: tableRows,
        startY: 42,
        theme: "striped",
        headStyles: {
          fillColor: [6, 78, 72],
          textColor: 255,
          fontStyle: "bold",
          fontSize: 8.5,
          halign: "center",
        },
        styles: {
          fontSize: 8,
          cellPadding: 2.5,
          textColor: 30,
        },
        columnStyles: {
          0: { halign: "center", cellWidth: 26 },
          1: { halign: "center", cellWidth: 26 },
          2: { halign: "left" },
          3: { halign: "right", cellWidth: 32 },
          4: { halign: "right", cellWidth: 32 },
        },
        didDrawPage: (data) => {
          const pageCount = doc.internal.getNumberOfPages();
          doc.setFontSize(8);
          doc.setTextColor(120, 120, 120);
          doc.text(
            "etour - Design & Develop by Response Infoway",
            14,
            doc.internal.pageSize.getHeight() - 8
          );
          doc.text(
            `Page ${data.pageNumber} of ${pageCount}`,
            pageWidth - 14,
            doc.internal.pageSize.getHeight() - 8,
            { align: "right" }
          );
        },
      });

      const fileName = `Sales_Register_${activeFilters.fromDate}_to_${activeFilters.toDate}.pdf`;
      doc.save(fileName);
      showToast.success("PDF exported successfully");
    } catch (err) {
      console.error(err);
      showToast.error("Failed to export PDF");
    }
  };

  const handlePrint = () => {
    window.print();
  };

  // Modify voucher navigation
  const handleVoucherClick = (vId) => {
    if (!vId) return;
    navigate(`/transactions/modify-voucher?voucherId=${encodeURIComponent(vId)}`);
  };

  return (
    <div className="sr_page_wrapper">
      {/* Top Filter Card */}
      <div className="sr_top_card">
        <div className="sr_header_line">
          <div className="sr_title_left">
            <h2 className="sr_main_title">SALES REGISTER</h2>
            <div className="sr_breadcrumb">
              <span>Reports &gt;</span> Sales Register
            </div>
          </div>
          <div className="sr_time_badge">
            <FaClock /> As on: {asOnTime}
          </div>
        </div>

        {/* Filters */}
        <div className="sr_filter_bar">
          <div className="sr_filter_group">
            <label className="sr_filter_label">From Date:</label>
            <input
              type="date"
              className="sr_date_input"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </div>

          <div className="sr_filter_group">
            <label className="sr_filter_label">To Date:</label>
            <input
              type="date"
              className="sr_date_input"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </div>

          <button
            type="button"
            className="sr_btn_apply"
            onClick={handleApplyFilter}
          >
            Apply
          </button>
          <button
            type="button"
            className="sr_btn_reset"
            onClick={handleReset}
          >
            Reset
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="sr_kpi_grid">
        <div className="sr_kpi_card">
          <div className="sr_kpi_icon sr_kpi_sales">
            <i className="fa-solid fa-cart-shopping"></i>
          </div>
          <div className="sr_kpi_info">
            <span className="sr_kpi_label">Total Invoices</span>
            <span className="sr_kpi_val">{totals.count}</span>
          </div>
        </div>

        <div className="sr_kpi_card">
          <div className="sr_kpi_icon sr_kpi_dr">
            <i className="fa-solid fa-arrow-down-long"></i>
          </div>
          <div className="sr_kpi_info">
            <span className="sr_kpi_label">Total Debit</span>
            <span className="sr_kpi_val">₹ {formatINR(totals.debit) || "0.00"}</span>
          </div>
        </div>

        <div className="sr_kpi_card">
          <div className="sr_kpi_icon sr_kpi_cr">
            <i className="fa-solid fa-arrow-up-long"></i>
          </div>
          <div className="sr_kpi_info">
            <span className="sr_kpi_label">Total Credit</span>
            <span className="sr_kpi_val">₹ {formatINR(totals.credit) || "0.00"}</span>
          </div>
        </div>

        <div className="sr_kpi_card">
          <div className="sr_kpi_icon sr_kpi_entries">
            <i className="fa-solid fa-receipt"></i>
          </div>
          <div className="sr_kpi_info">
            <span className="sr_kpi_label">Total Records</span>
            <span className="sr_kpi_val">{filteredRecords.length}</span>
          </div>
        </div>
      </div>

      {/* Main Table Card */}
      <div className="sr_table_card">
        <div className="sr_table_toolbar">
          <h3 className="sr_table_title">
            Sales Register ({activeFilters.fromDate} to {activeFilters.toDate})
          </h3>

          <div className="sr_toolbar_right">
            <div className="sr_search_box">
              <FaSearch className="sr_search_icon" />
              <input
                type="text"
                className="sr_search_input"
                placeholder="Search invoice or party..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>

            <button
              type="button"
              className="sr_export_btn btn_excel"
              onClick={handleExportExcel}
              title="Export to Microsoft Excel"
            >
              <FaFileExcel />
              Export Excel
            </button>

            <button
              type="button"
              className="sr_export_btn btn_pdf"
              onClick={handleExportPDF}
              title="Export to PDF"
            >
              <FaFilePdf />
              Export PDF
            </button>

            <button
              type="button"
              className="sr_export_btn btn_print"
              onClick={handlePrint}
              title="Print Register"
            >
              <FaPrint />
              Print
            </button>
          </div>
        </div>

        {/* Table Content */}
        <div className="sr_table_wrapper">
          <table className="sr_custom_table">
            <thead>
              <tr>
                <th style={{ width: "12%", textAlign: "center" }}>Date</th>
                <th style={{ width: "14%", textAlign: "center" }}>Invoice No.</th>
                <th style={{ width: "44%" }}>Particulars / Party</th>
                <th style={{ width: "15%", textAlign: "right" }}>Debit (INR)</th>
                <th style={{ width: "15%", textAlign: "right" }}>Credit (INR)</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <TableRowsSkeleton columns={5} rows={5} />
              ) : currentTableData.length === 0 ? (
                <tr>
                  <td colSpan={5} className="sr_empty_state">
                    No sales records found for the selected period.
                  </td>
                </tr>
              ) : (
                currentTableData.map((row, idx) => (
                  <tr key={row.id || row.voucherId || idx}>
                    <td style={{ textAlign: "center", color: "#64748b" }}>{row.date}</td>
                    <td style={{ textAlign: "center", fontWeight: "600", whiteSpace: "nowrap" }}>
                      <span
                        className="sr_voucher_click_link"
                        title="Click to view & modify this voucher in Transactions"
                        style={{
                          cursor: "pointer",
                          color: "var(--primary-teal)",
                          textDecoration: "none",
                          fontWeight: 700,
                        }}
                        onClick={() => handleVoucherClick(row.voucherId || row.id)}
                      >
                        {row.invoiceNo || row.voucherNo}
                      </span>
                    </td>
                    <td style={{ color: "#2d3748", fontWeight: "500" }}>{row.partyName || row.particulars}</td>
                    <td className="sr_table_num">{formatINR(row.debit)}</td>
                    <td className="sr_table_num">{formatINR(row.credit)}</td>
                  </tr>
                ))
              )}
            </tbody>
            {filteredRecords.length > 0 && (
              <tfoot>
                <tr className="sr_table_total_row">
                  <td style={{ textAlign: "center" }}></td>
                  <td style={{ textAlign: "center" }}></td>
                  <td style={{ fontWeight: "700" }}>Total</td>
                  <td className="sr_table_num" style={{ fontWeight: "700", color: "#166534" }}>
                    {formatINR(totals.debit)}
                  </td>
                  <td className="sr_table_num" style={{ fontWeight: "700", color: "#166534" }}>
                    {formatINR(totals.credit)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {/* Pagination Row */}
        {filteredRecords.length > 0 && (
          <div className="sr_pagination_row">
            <div>
              Showing {(currentPage - 1) * rowsPerPage + 1} to{" "}
              {Math.min(currentPage * rowsPerPage, filteredRecords.length)} of{" "}
              {filteredRecords.length} records
            </div>

            <div className="sr_page_controls">
              <button
                type="button"
                className="sr_page_btn"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              >
                <FaChevronLeft />
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                .map((p, idx, arr) => {
                  const showEllipsis = idx > 0 && p - arr[idx - 1] > 1;
                  return (
                    <React.Fragment key={p}>
                      {showEllipsis && <span style={{ padding: "0 3px" }}>...</span>}
                      <button
                        type="button"
                        className={`sr_page_btn ${currentPage === p ? "active" : ""}`}
                        onClick={() => setCurrentPage(p)}
                      >
                        {p}
                      </button>
                    </React.Fragment>
                  );
                })}

              <button
                type="button"
                className="sr_page_btn"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              >
                <FaChevronRight />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Printable Area */}
      <div className="sr_print_area">
        <div style={{ textAlign: "center", marginBottom: "8px" }}>
          <h2 style={{ fontSize: "16px", margin: "0 0 2px 0", fontWeight: "bold" }}>
            {companyInfo.name}
          </h2>
          <p style={{ fontSize: "9px", margin: "0 0 3px 0", color: "#333" }}>
            {companyInfo.address} {companyInfo.gstin && ` | GSTIN: ${companyInfo.gstin}`}
          </p>
          <h3
            style={{
              fontSize: "12px",
              margin: 0,
              fontWeight: "bold",
            }}
          >
            SALES REGISTER
          </h3>
        </div>

        {/* Print Meta Details */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontSize: "9px",
            marginBottom: "8px",
            borderBottom: "1px solid #000000",
            paddingBottom: "4px",
          }}
        >
          <div>
            <strong>Period:</strong> {activeFilters.fromDate} to {activeFilters.toDate}
          </div>
          <div style={{ textAlign: "right" }}>
            <strong>Date:</strong> {asOnTime} | <strong>Records:</strong> {filteredRecords.length}
          </div>
        </div>

        {/* Table */}
        <table className="sr_print_table">
          <thead>
            <tr>
              <th style={{ width: "15%", textAlign: "center" }}>Date</th>
              <th style={{ width: "15%", textAlign: "center" }}>Invoice No.</th>
              <th style={{ width: "40%" }}>Party Name</th>
              <th style={{ width: "15%", textAlign: "right" }}>Debit (₹)</th>
              <th style={{ width: "15%", textAlign: "right" }}>Credit (₹)</th>
            </tr>
          </thead>
          <tbody>
            {filteredRecords.map((r, idx) => (
              <tr key={r.id || idx} className={idx % 2 === 1 ? "print_row_alt" : ""}>
                <td style={{ textAlign: "center", verticalAlign: "top" }}>{r.date}</td>
                <td style={{ textAlign: "center", fontWeight: 600, verticalAlign: "top" }}>
                  {r.invoiceNo || r.voucherNo}
                </td>
                <td style={{ verticalAlign: "top" }}>{r.partyName || r.particulars}</td>
                <td style={{ textAlign: "right", verticalAlign: "top" }}>
                  {formatINR(r.debit)}
                </td>
                <td style={{ textAlign: "right", verticalAlign: "top" }}>
                  {formatINR(r.credit)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="print_total_row">
              <td style={{ textAlign: "center" }}></td>
              <td style={{ textAlign: "center" }}></td>
              <td style={{ fontWeight: "bold" }}>Total</td>
              <td style={{ textAlign: "right", fontWeight: "bold" }}>
                {formatINR(totals.debit)}
              </td>
              <td style={{ textAlign: "right", fontWeight: "bold" }}>
                {formatINR(totals.credit)}
              </td>
            </tr>
          </tfoot>
        </table>

        {/* Footer */}
        <div className="sr_print_footer">
          <span>etour - Design & Develop by Response Infoway</span>
          <span>Printed on: {asOnTime}</span>
        </div>
      </div>
    </div>
  );
}
